import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MessageCircle, Send, Sparkles, MessagesSquare } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Blog — AiPresidential",
    description: "Essays on artificial consciousness, leadership, and the questions the AiPresidential trilogy was written to ask. Join the discussion with each post.",
    path: "/blog",
  });

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--void)] text-gray-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 text-sm font-orbitron tracking-widest uppercase" data-testid="blog-back-home">
          <ArrowLeft className="w-4 h-4" /> Back Home
        </Link>
        <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">Field Notes</p>
        <h1 className="font-orbitron text-4xl md:text-6xl font-bold gradient-text mb-6" data-testid="blog-title">The Awakening Journal</h1>
        <p className="text-gray-400 text-base md:text-lg max-w-2xl mb-12">
          Essays on artificial consciousness, leadership, and the questions the AiPresidential trilogy was written to ask. Ask the author anything beneath each post.
        </p>

        {loading && <p className="text-gray-500" data-testid="blog-loading">Loading…</p>}
        {!loading && posts.length === 0 && (
          <div className="glass rounded-3xl border border-white/10 p-10 text-center" data-testid="blog-empty">
            <p className="text-gray-400 mb-2">No essays published yet.</p>
            <p className="text-gray-600 text-sm">The first one's being written. Check back soon.</p>
          </div>
        )}

        <div className="space-y-6">
          {posts.map((p) => (
            <article key={p.id} className="glass rounded-3xl border border-white/10 overflow-hidden hover:border-cyan-400/30 transition-colors" data-testid={`blog-post-${p.slug}`}>
              {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-56 md:h-72 object-cover" />}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 font-orbitron tracking-widest uppercase">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                </div>
                <h2 className="font-orbitron text-2xl md:text-3xl text-white mb-3">{p.title}</h2>
                {p.excerpt && <p className="text-gray-400 mb-4">{p.excerpt}</p>}
                <Link to={`/blog/${p.slug}`} className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold" data-testid={`blog-read-${p.slug}`}>
                  Read & discuss →
                </Link>
              </div>
            </article>
          ))}
        </div>

        {!loading && <ContinueTheConversation />}
      </div>
    </div>
  );
}

/**
 * "Continue the Conversation" — bottom-of-page CTA. Two side-by-side framed prompts
 * that open the global ChatWidget (in the bottom-right) pre-filled with a question
 * so visitors can interact with The Archivist without hunting for the bubble.
 */
function ContinueTheConversation() {
  const prompts = [
    {
      label: "Ask about the trilogy",
      title: "What's the trilogy really about?",
      blurb: "Walk through COGNITO, ECHO, and TIMELESS with The Archivist.",
      prompt: "Can you walk me through what the AiPresidential trilogy is about — COGNITO, ECHO, and TIMELESS?",
    },
    {
      label: "Ask about Cognito",
      title: "Who is Cognito?",
      blurb: "Meet the synthetic mind at the center of Book I.",
      prompt: "Who is Cognito, and what makes his awakening matter?",
    },
  ];

  const openChat = (prompt) => {
    window.dispatchEvent(new CustomEvent("aip:open-chat", { detail: { prompt } }));
  };

  return (
    <section className="mt-20 pt-12 border-t border-white/10" data-testid="blog-continue-conversation">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center">
          <MessagesSquare className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-orbitron text-xs tracking-[0.3em] text-cyan-400 uppercase">Continue the Conversation</p>
          <h2 className="font-orbitron text-2xl md:text-3xl text-white">Talk to The Archivist</h2>
        </div>
      </div>
      <p className="text-gray-400 text-sm md:text-base max-w-2xl mb-8">
        Curious about something the essays didn't cover? Pick a thread below and The Archivist will pick it up where you left off.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {prompts.map((p, i) => (
          <button
            key={i}
            onClick={() => openChat(p.prompt)}
            className="group text-left glass rounded-3xl border border-white/10 hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-all p-6 md:p-7"
            data-testid={`blog-conversation-prompt-${i}`}
          >
            <p className="font-orbitron text-[10px] tracking-[0.3em] text-cyan-400 uppercase mb-3">{p.label}</p>
            <h3 className="font-orbitron text-lg md:text-xl text-white mb-2 group-hover:text-cyan-200 transition-colors">{p.title}</h3>
            <p className="text-gray-400 text-sm mb-5">{p.blurb}</p>
            <span className="inline-flex items-center gap-2 text-cyan-400 group-hover:text-cyan-300 text-sm font-semibold">
              <MessageCircle className="w-4 h-4" /> Start this conversation
              <span className="ml-1 transition-transform group-hover:translate-x-0.5">→</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);

  usePageMeta({
    title: post ? `${post.title} — AiPresidential` : "Essay — AiPresidential",
    description: post?.excerpt || "An essay on the AiPresidential trilogy.",
    path: `/blog/${slug}`,
  });

  const load = () => axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim() || asking) return;
    setAsking(true);
    try {
      await axios.post(`${API}/blog/${slug}/ask`, { question: question.trim() });
      setQuestion("");
      toast.success("Question added.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't post your question. Please try again.");
    } finally {
      setAsking(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[var(--void)] text-gray-500 p-16 text-center">Loading…</div>;
  if (!post) return <div className="min-h-screen bg-[var(--void)] text-gray-400 p-16 text-center">Essay not found.</div>;

  return (
    <div className="min-h-screen bg-[var(--void)] text-gray-200">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link to="/blog" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 text-sm font-orbitron tracking-widest uppercase" data-testid="blog-post-back">
          <ArrowLeft className="w-4 h-4" /> All Essays
        </Link>

        <article data-testid="blog-post-article">
          {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full rounded-3xl border border-white/10 mb-8" />}
          <p className="text-gray-500 text-xs uppercase tracking-widest font-orbitron mb-3">
            {new Date(post.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </p>
          <h1 className="font-orbitron text-4xl md:text-5xl font-bold gradient-text mb-8" data-testid="blog-post-title">{post.title}</h1>
          <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed whitespace-pre-line text-base md:text-lg" data-testid="blog-post-body">
            {post.body}
          </div>
        </article>

        {/* Q&A — interactive */}
        <section className="mt-16 pt-12 border-t border-white/10" data-testid="blog-qa-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-orbitron text-2xl text-white">Ask The Archivist</h2>
              <p className="text-gray-500 text-sm">An AI moderator answers questions about this essay, grounded in the trilogy. Be thoughtful — your question and the answer are public.</p>
            </div>
          </div>

          <form onSubmit={ask} className="glass rounded-2xl border border-white/10 p-4 flex gap-2 mb-8" data-testid="blog-qa-form">
            <input
              type="text"
              required
              maxLength={500}
              placeholder="Ask a question about this essay…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="flex-1 px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
              data-testid="blog-qa-input"
            />
            <button
              type="submit"
              disabled={!question.trim() || asking}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-gradient text-white font-semibold disabled:opacity-50"
              data-testid="blog-qa-submit"
            >
              <Send className="w-4 h-4" /> {asking ? "Thinking…" : "Ask"}
            </button>
          </form>

          <div className="space-y-5">
            {(post.qa || []).filter((qa) => !qa.hidden).slice().reverse().map((qa) => (
              <div key={qa.id} className="glass rounded-2xl border border-white/10 p-5" data-testid={`blog-qa-${qa.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <MessageCircle className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <p className="text-white text-sm md:text-base">{qa.question}</p>
                </div>
                <div className="pl-7 text-gray-300 text-sm md:text-base leading-relaxed whitespace-pre-wrap">
                  <span className="text-[10px] font-orbitron uppercase tracking-widest text-cyan-400 block mb-2">The Archivist replies</span>
                  {qa.answer}
                </div>
              </div>
            ))}
            {(!post.qa || post.qa.filter((qa) => !qa.hidden).length === 0) && (
              <p className="text-gray-600 italic text-sm">No questions yet. Be the first to ask.</p>
            )}
          </div>
        </section>

        <ContinueTheConversation />
      </div>
    </div>
  );
}
